import { FileText, Scale, Users, Clock, CheckCircle, Phone } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const ProbateAdministration = () => {
  const mainContent = (
    <>
      {/* Overview Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Texas Probate Administration Services</h2>
            <p className="text-lg text-muted-foreground mb-8">
              When a loved one passes away, navigating the probate process can be overwhelming. Our experienced probate attorneys guide you through every step, handling the legal complexities with compassion and professionalism.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <FileText className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Independent Administration</h3>
                <p className="text-muted-foreground mb-4">
                  The most common and efficient form of probate in Texas, allowing the executor to act with minimal court supervision.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Faster process with less court involvement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Lower costs and fees</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Executor has greater flexibility</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Scale className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Dependent Administration</h3>
                <p className="text-muted-foreground mb-4">
                  Required when there's no will or the will doesn't allow independent administration.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Court oversight of all major decisions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Protection for complex estates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Additional safeguards for beneficiaries</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">The Probate Process</h3>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "File Application for Probate",
                  desc: "We prepare and file the application with the appropriate county court, including the original will if available."
                },
                {
                  step: "2",
                  title: "Court Hearing",
                  desc: "Attend court hearing to prove the will's validity and appoint the executor or administrator."
                },
                {
                  step: "3",
                  title: "Inventory & Appraisal",
                  desc: "Identify, gather, and value all estate assets within 90 days of appointment."
                },
                {
                  step: "4",
                  title: "Notify Creditors",
                  desc: "Provide legal notice to creditors and manage claims against the estate."
                },
                {
                  step: "5",
                  title: "Pay Debts & Taxes",
                  desc: "Settle outstanding debts, file final tax returns, and pay estate taxes if applicable."
                },
                {
                  step: "6",
                  title: "Distribute Assets",
                  desc: "Distribute remaining assets to beneficiaries according to the will or state law."
                },
                {
                  step: "7",
                  title: "Close Estate",
                  desc: "File final accounting with the court and obtain order closing the estate."
                }
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center text-sm">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Types of Probate */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">When is Probate Required?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-accent">Probate Required</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Real estate in deceased's name only</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Bank accounts without beneficiaries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Personal property of significant value</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Assets with no clear ownership transfer</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-accent">May Avoid Probate</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Property held in living trust</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Joint tenancy with right of survivorship</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Accounts with TOD/POD designations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Small estates under $75,000</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Our Probate Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Users className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Compassionate Guidance</h3>
                <p className="text-muted-foreground">We understand this is a difficult time and provide supportive, empathetic service</p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Clock className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Efficient Process</h3>
                <p className="text-muted-foreground">We streamline the probate process to minimize delays and expenses</p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Scale className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Experienced Representation</h3>
                <p className="text-muted-foreground">Years of experience handling complex probate matters in Texas courts</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Probate Administration"
      subtitle="Guiding Families Through the Texas Probate Process with Compassion and Expertise"
      icon={<FileText className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default ProbateAdministration;